import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Box, Center, Text, VStack, Button, Badge } from '@chakra-ui/react';
import { FaUserShield, FaUsers, FaChartLine, FaCog, FaCalculator } from 'react-icons/fa';
import LoadingScreen from '../common/LoadingScreen';

// Admin-only route
export const AdminRoute = ({ children, fallbackPath = '/dashboard' }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <LoadingScreen height="100vh" label="Verifying admin privileges..." />
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <Center minH="100vh">
        <VStack spacing={6} textAlign="center" maxW="500px" p={8}>
          <Box color="red.500" fontSize="6xl">
            <FaUserShield />
          </Box>
          <Text fontSize="2xl" fontWeight="bold" color="red.500">
            Admin Access Required
          </Text>
          <Text color="gray.600">
            This page is restricted to administrators only.
          </Text>
          <VStack spacing={2}>
            <Badge colorScheme="blue" fontSize="md" p={2}>
              Your Role: {user?.role || 'Guest'}
            </Badge>
            <Badge colorScheme="red" fontSize="md" p={2}>
              Required: Admin
            </Badge>
          </VStack>
          <VStack spacing={3} w="100%">
            <Button
              colorScheme="blue"
              onClick={() => window.location.href = fallbackPath}
              w="100%"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              w="100%"
            >
              Go Back
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return children;
};

// Staff route (admin, sub-admin, accountant)
export const StaffRoute = ({ children, fallbackPath = '/dashboard' }) => {
  const { user, loading } = useAuth();
  const staffRoles = ['admin', 'sub-admin', 'accountant'];

  if (loading) {
    return (
      <LoadingScreen height="100vh" label="Verifying staff privileges..." />
    );
  }

  if (!user || !staffRoles.includes(user.role)) {
    return (
      <Center minH="100vh">
        <VStack spacing={6} textAlign="center" maxW="500px" p={8}>
          <Box color="orange.500" fontSize="6xl">
            <FaUsers />
          </Box>
          <Text fontSize="2xl" fontWeight="bold" color="orange.500">
            Staff Access Required
          </Text>
          <Text color="gray.600">
            This page is restricted to temple staff members.
          </Text>
          <VStack spacing={2}>
            <Badge colorScheme="blue" fontSize="md" p={2}>
              Your Role: {user?.role || 'Guest'}
            </Badge>
            <Badge colorScheme="orange" fontSize="md" p={2}>
              Required: Staff Member
            </Badge>
          </VStack>
          <VStack spacing={3} w="100%">
            <Button
              colorScheme="blue"
              onClick={() => window.location.href = fallbackPath}
              w="100%"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              w="100%"
            >
              Go Back
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return children;
};

// Devotee route (authenticated users)
export const DevoteeRoute = ({ children, fallbackPath = '/login' }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <LoadingScreen height="100vh" label="Verifying devotee status..." />
    );
  }

  if (!user) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
};

// Analytics route (admin, sub-admin)
export const AnalyticsRoute = ({ children, fallbackPath = '/dashboard' }) => {
  const { user, loading } = useAuth();
  const analyticsRoles = ['admin', 'sub-admin'];

  if (loading) {
    return (
      <LoadingScreen height="100vh" label="Verifying analytics access..." />
    );
  }

  if (!user || !analyticsRoles.includes(user.role)) {
    return (
      <Center minH="100vh">
        <VStack spacing={6} textAlign="center" maxW="500px" p={8}>
          <Box color="purple.500" fontSize="6xl">
            <FaChartLine />
          </Box>
          <Text fontSize="2xl" fontWeight="bold" color="purple.500">
            Analytics Access Required
          </Text>
          <Text color="gray.600">
            This page is restricted to users with analytics privileges.
          </Text>
          <VStack spacing={2}>
            <Badge colorScheme="blue" fontSize="md" p={2}>
              Your Role: {user?.role || 'Guest'}
            </Badge>
            <Badge colorScheme="purple" fontSize="md" p={2}>
              Required: Admin or Sub-Admin
            </Badge>
          </VStack>
          <VStack spacing={3} w="100%">
            <Button
              colorScheme="blue"
              onClick={() => window.location.href = fallbackPath}
              w="100%"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              w="100%"
            >
              Go Back
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return children;
};

// Settings route (admin, sub-admin)
export const SettingsRoute = ({ children, fallbackPath = '/dashboard' }) => {
  const { user, loading } = useAuth();
  const settingsRoles = ['admin', 'sub-admin'];

  if (loading) {
    return (
      <LoadingScreen height="100vh" label="Verifying settings access..." />
    );
  }

  if (!user || !settingsRoles.includes(user.role)) {
    return (
      <Center minH="100vh">
        <VStack spacing={6} textAlign="center" maxW="500px" p={8}>
          <Box color="teal.500" fontSize="6xl">
            <FaCog />
          </Box>
          <Text fontSize="2xl" fontWeight="bold" color="teal.500">
            Settings Access Required
          </Text>
          <Text color="gray.600">
            This page is restricted to users with settings privileges.
          </Text>
          <VStack spacing={2}>
            <Badge colorScheme="blue" fontSize="md" p={2}>
              Your Role: {user?.role || 'Guest'}
            </Badge>
            <Badge colorScheme="teal" fontSize="md" p={2}>
              Required: Admin or Sub-Admin
            </Badge>
          </VStack>
          <VStack spacing={3} w="100%">
            <Button
              colorScheme="blue"
              onClick={() => window.location.href = fallbackPath}
              w="100%"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              w="100%"
            >
              Go Back
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return children;
};

// Sub-admin route (sub-admin only)
export const SubAdminRoute = ({ children, fallbackPath = '/dashboard' }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <LoadingScreen height="100vh" label="Verifying sub-admin privileges..." />
    );
  }

  if (!user || user.role !== 'sub-admin') {
    return (
      <Center minH="100vh">
        <VStack spacing={6} textAlign="center" maxW="500px" p={8}>
          <Box color="blue.500" fontSize="6xl">
            <FaUsers />
          </Box>
          <Text fontSize="2xl" fontWeight="bold" color="blue.500">
            Sub-Admin Access Required
          </Text>
          <Text color="gray.600">
            This page is restricted to sub-administrators only.
          </Text>
          <VStack spacing={2}>
            <Badge colorScheme="blue" fontSize="md" p={2}>
              Your Role: {user?.role || 'Guest'}
            </Badge>
            <Badge colorScheme="blue" fontSize="md" p={2}>
              Required: Sub-Admin
            </Badge>
          </VStack>
          <VStack spacing={3} w="100%">
            <Button
              colorScheme="blue"
              onClick={() => window.location.href = fallbackPath}
              w="100%"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              w="100%"
            >
              Go Back
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return children;
};

// Accountant route (admin, accountant)
export const AccountantRoute = ({ children, fallbackPath = '/dashboard' }) => {
  const { user, loading } = useAuth();
  const accountantRoles = ['admin', 'accountant'];

  if (loading) {
    return (
      <LoadingScreen height="100vh" label="Verifying accountant privileges..." />
    );
  }

  if (!user || !accountantRoles.includes(user.role)) {
    return (
      <Center minH="100vh">
        <VStack spacing={6} textAlign="center" maxW="500px" p={8}>
          <Box color="green.500" fontSize="6xl">
            <FaCalculator />
          </Box>
          <Text fontSize="2xl" fontWeight="bold" color="green.500">
            Accountant Access Required
          </Text>
          <Text color="gray.600">
            This page is restricted to users with accounting privileges.
          </Text>
          <VStack spacing={2}>
            <Badge colorScheme="blue" fontSize="md" p={2}>
              Your Role: {user?.role || 'Guest'}
            </Badge>
            <Badge colorScheme="green" fontSize="md" p={2}>
              Required: Admin or Accountant
            </Badge>
          </VStack>
          <VStack spacing={3} w="100%">
            <Button
              colorScheme="blue"
              onClick={() => window.location.href = fallbackPath}
              w="100%"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              w="100%"
            >
              Go Back
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return children;
};
